import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import Estado from '../components/ui/Estado';
import PageHeader from '../components/ui/PageHeader';
import Icon from '../components/ui/Icon';
import { toast } from '../components/Toast';
import { fmtBRL, fmtData } from '../utils/format';
import {
  idNotificacaoUsuario,
  idNotificacaoConta,
  idNotificacaoManutencao,
  marcarTodasComoVistas,
} from '../utils/notificacoes';

/** Traduz "faltam N dias" no tom do cartão (neutro / atenção / atraso). */
function tomPorPrazo(dias) {
  if (dias == null) return '';
  if (dias < 0) return 'atraso';
  if (dias <= 1) return 'proximo';
  return '';
}

function textoPrazo(dias, { atrasadoSing, hoje, futuro }) {
  if (dias == null) return null;
  if (dias < 0) return `${atrasadoSing} há ${Math.abs(dias)} dia(s)`;
  if (dias === 0) return hoje;
  return `${futuro} ${dias} dia(s)`;
}

export { getNotificacoesVistas, marcarTodasComoVistas } from '../utils/notificacoes';

export default function Notificacoes() {
  const [usuariosPendentes, setUsuariosPendentes] = useState([]);
  const [avisosContas, setAvisosContas] = useState([]);
  const [avisosManutencao, setAvisosManutencao] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const isAdmin = localStorage.getItem('role') === 'ADMIN';

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [contas, manutencoes, usuarios] = await Promise.all([
        api.getAvisosContasAPagar(),
        api.getAvisosManutencaoMaquinario(),
        isAdmin ? api.getUsuariosPendentes() : Promise.resolve([]),
      ]);
      setAvisosContas(contas || []);
      setAvisosManutencao(manutencoes || []);
      setUsuariosPendentes(usuarios || []);
      const ids = [
        ...(usuarios || []).map(idNotificacaoUsuario),
        ...(contas || []).map(idNotificacaoConta),
        ...(manutencoes || []).map(idNotificacaoManutencao),
      ];
      marcarTodasComoVistas(ids);
    } catch (e) {
      setErro(e.message || 'Não foi possível carregar as notificações.');
    } finally {
      setCarregando(false);
    }
  }, [isAdmin]);

  useEffect(() => { carregar(); }, [carregar]);

  const aprovar = async (u) => {
    try {
      await api.aprovarUsuario(u.id);
      toast(`${u.nome} agora tem acesso à fazenda.`, 'success');
      carregar();
    } catch (e) { toast(e.message, 'error'); }
  };

  const recusar = async (u) => {
    try {
      await api.excluirUsuario(u.id);
      toast(`Solicitação de ${u.nome} recusada.`, 'success');
      carregar();
    } catch (e) { toast(e.message, 'error'); }
  };

  const marcarPaga = async (aviso) => {
    try {
      // Recorrentes: paga o ciclo vigente com o valor estimado (quando houver).
      // Se não houver estimativa, o usuário deve confirmar o valor em Contas a
      // pagar, então mandamos o usuário para lá com uma mensagem clara.
      if (aviso.recorrente && aviso.valor == null) {
        toast('Informe o valor deste ciclo em Contas a pagar.', 'info');
        return;
      }
      const body = aviso.valor != null ? { valor: aviso.valor } : undefined;
      await api.marcarContaPaga(aviso.id, body);
      toast('Conta marcada como paga.', 'success');
      carregar();
    } catch (e) { toast(e.message, 'error'); }
  };

  const vazio = usuariosPendentes.length === 0
    && avisosContas.length === 0
    && avisosManutencao.length === 0;

  return (
    <div>
      <PageHeader
        titulo="Notificações"
        icone="notificacao"
        descricao={`Contas a pagar, manutenções de maquinário${isAdmin ? ' e solicitações de acesso à fazenda' : ''}.`}
      />

      <div className="card">
        <Estado
          carregando={carregando}
          erro={erro}
          aoTentarNovamente={carregar}
          vazio={vazio}
          vazioProps={{
            icone: 'notificacao',
            titulo: 'Sem novas notificações',
            descricao: isAdmin
              ? 'Avisos de contas (2 dias), manutenções (7 dias) e pedidos de acesso aparecem aqui.'
              : 'Avisos de contas a pagar (2 dias) e manutenções de maquinário (7 dias) aparecem aqui.',
          }}
          itens={3}
        >
        <div className="notif-lista">
          {avisosManutencao.map((a) => {
            const tom = tomPorPrazo(a.diasParaVencer);
            const prazo = textoPrazo(a.diasParaVencer, {
              atrasadoSing: 'Atrasada', hoje: 'É hoje', futuro: 'Em',
            });
            return (
              <div key={`m-${a.id}`} className={`notif ${tom ? `notif--${tom}` : ''}`}>
                <span className="notif__icone" aria-hidden="true">
                  <Icon nome="manutencao" tamanho={18} />
                </span>
                <div className="notif__corpo">
                  <div className="notif__titulo">
                    {a.titulo || `Manutenção — ${a.maquinarioNome}`}
                  </div>
                  <div className="notif__meta">
                    Prevista para {fmtData(a.proximaManutencaoData)}
                    {a.proximaManutencaoHorimetro != null
                      ? ` · Horímetro ${a.proximaManutencaoHorimetro} h`
                      : ''}
                  </div>
                  {prazo && <span className="notif__prazo">{prazo}</span>}
                  <div className="notif__acoes">
                    <Link to="/maquinario" className="btn-secondary btn-sm">Ver maquinário</Link>
                  </div>
                </div>
              </div>
            );
          })}

          {avisosContas.map((a) => {
            const tom = tomPorPrazo(a.diasParaVencer);
            const prazo = textoPrazo(a.diasParaVencer, {
              atrasadoSing: 'Vencida', hoje: 'Vence hoje', futuro: 'Vence em',
            });
            return (
              <div key={`c-${a.id}`} className={`notif ${tom ? `notif--${tom}` : ''}`}>
                <span className="notif__icone" aria-hidden="true">
                  <Icon nome="conta-pagar" tamanho={18} />
                </span>
                <div className="notif__corpo">
                  <div className="notif__titulo">
                    {a.titulo || `Pagamento — ${a.beneficiario}`}
                  </div>
                  <div className="notif__meta">
                    Vencimento {fmtData(a.dataVencimento)} · {fmtBRL(a.valor)}
                    {a.descricao ? ` · ${a.descricao}` : ''}
                  </div>
                  {prazo && <span className="notif__prazo">{prazo}</span>}
                  {a.recorrente && (
                    <span className="selo selo--info notif__tag">Recorrente</span>
                  )}
                  <div className="notif__acoes">
                    {!(a.recorrente && a.valor == null) && (
                      <button type="button" className="btn-primary btn-sm" onClick={() => marcarPaga(a)}>
                        <Icon nome="ok" tamanho={14} />
                        Marcar como paga
                      </button>
                    )}
                    <Link to="/contas-a-pagar" className="btn-secondary btn-sm">
                      {a.recorrente && a.valor == null ? 'Informar valor' : 'Ver contas'}
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}

          {usuariosPendentes.map((u) => (
            <div key={`u-${u.id}`} className="notif">
              <span className="notif__icone" aria-hidden="true">
                <Icon nome="usuario" tamanho={18} />
              </span>
              <div className="notif__corpo">
                <div className="notif__titulo">{u.nome} quer entrar na fazenda</div>
                {u.email && <div className="notif__meta">{u.email}</div>}
                <span className="notif__prazo">Aguardando sua aprovação para acessar os dados.</span>
                <div className="notif__acoes">
                  <button type="button" className="btn-primary btn-sm" onClick={() => aprovar(u)}>
                    <Icon nome="ok" tamanho={14} />
                    Aprovar
                  </button>
                  <button type="button" className="btn-secondary btn-sm" onClick={() => recusar(u)}>
                    <Icon nome="fechar" tamanho={14} />
                    Recusar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Estado>
      </div>
    </div>
  );
}
